// Send Order: one click = one order = one supplier. Suppliers offered are only
// those with awarded (ACCEPTED) quotes not yet ordered; picking one filters the
// item checklist (quantity editable, defaults to the item volume) and the SAM
// dropdown. Already-ordered quotes render disabled with an "Ordered" badge — the
// backend re-order guard (quote.orderId) makes this terminal. Multi-supplier
// inquiries: send once per supplier, re-opening the modal.

import {
  Badge,
  Box,
  Button,
  ChakraSelect,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  useToast,
} from '@elkaso-app/web-design';
import {
  InquiryQuoteStatus,
  RestaurantInquiry,
  RestaurantInquiryItem,
  RestaurantInquiryQuote,
  useGetInquiryRestaurantBranchesApi,
  useGetInquirySupplierUsersApi,
  useSendInquiryOrderApi,
} from 'apis/restaurant-inquiries';
import { useGetSupplierProfileCommissionApi } from 'apis/supplier-profiles';
import { apiErrorMessage } from 'pages/inquiry-processor/utils/inquiry-actions';
import { numOnly } from 'pages/inquiry-processor/utils/num-only';
import { isPriced } from 'pages/inquiry-processor/utils/quote-slots';
import { useEffect, useMemo, useState } from 'react';
import { FiMail, FiPhone } from 'react-icons/fi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  inquiry: RestaurantInquiry;
}

type Option = { value: number; label: string };
// SAM options carry contacts so the menu can render a two-line contact row.
type SamOption = Option & { email?: string | null; phoneNumber?: string | null };

interface AwardedRow {
  quote: RestaurantInquiryQuote;
  item: RestaurantInquiryItem;
}

// SAM menu options render as a two-line contact row — name on top, a muted
// icon-annotated email/phone line below — while the closed control shows just
// the name so it never wraps. (ChakraSelect's Option type is `unknown`, hence
// the casts.)
const formatSamOption = (option: unknown, meta: { context: string }) => {
  const opt = option as SamOption;
  if (meta.context !== 'menu') return opt.label;
  // Nameless users use their email as the label — don't repeat it below.
  const email = opt.email && opt.email !== opt.label ? opt.email : null;
  if (!email && !opt.phoneNumber) return opt.label;
  return (
    <Box py='0.5'>
      <Text fontSize='sm' fontWeight='600' lineHeight='1.3' noOfLines={1}>
        {opt.label}
      </Text>
      <Flex align='center' gap='3' mt='0.5' color='gray.500' fontSize='xs' lineHeight='1.2'>
        {email && (
          <Flex align='center' gap='1' minW='0'>
            <FiMail size={11} style={{ flexShrink: 0 }} />
            <Text as='span' noOfLines={1}>
              {email}
            </Text>
          </Flex>
        )}
        {opt.phoneNumber && (
          <Flex align='center' gap='1' flexShrink={0}>
            <FiPhone size={11} />
            <Text as='span'>{opt.phoneNumber}</Text>
          </Flex>
        )}
      </Flex>
    </Box>
  );
};

// The default filter only searches the label (name) — extend it so typing an
// email or phone number still narrows the list.
const filterSamOption = (candidate: { data: unknown }, input: string) => {
  if (!input) return true;
  const opt = candidate.data as SamOption;
  const query = input.toLowerCase();
  return [opt.label, opt.email, opt.phoneNumber].filter(Boolean).some((v) => String(v).toLowerCase().includes(query));
};

export const SendOrderModal = ({ isOpen, onClose, inquiry }: Props) => {
  const toast = useToast();
  const sendOrder = useSendInquiryOrderApi();

  // All awarded quotes on the inquiry, paired with their item (for volume/name).
  const awardedRows = useMemo<AwardedRow[]>(
    () =>
      (inquiry.items ?? []).flatMap((item) =>
        (item.quotes ?? [])
          .filter((quote) => quote.status === InquiryQuoteStatus.ACCEPTED)
          .map((quote) => ({ quote, item }))
      ),
    [inquiry.items]
  );

  // Suppliers offered: only those with at least one awarded, not-yet-ordered quote.
  const supplierOptions = useMemo<Option[]>(() => {
    const byId = new Map<number, string>();
    awardedRows.forEach(({ quote }) => {
      if (!quote.orderId && !byId.has(quote.supplierId)) {
        byId.set(quote.supplierId, quote.supplier?.name || `Supplier #${quote.supplierId}`);
      }
    });
    return [...byId.entries()].map(([value, label]) => ({ value, label }));
  }, [awardedRows]);

  const [supplier, setSupplier] = useState<Option | null>(null);
  const [branch, setBranch] = useState<Option | null>(null);
  const [sam, setSam] = useState<Option | null>(null);
  // Special requests from either party — lands on the LPO's Additional Comments
  // (after the supplier's payment terms).
  const [comment, setComment] = useState('');
  // Unchecked (default) = the BE produces no order notification queues at all
  // (no email/WhatsApp/chat/push to either party). The order is created either way.
  const [sendNotifications, setSendNotifications] = useState(false);
  // quoteId -> { checked, quantity } for the selected supplier's sendable quotes.
  const [selection, setSelection] = useState<Record<number, { checked: boolean; quantity: string }>>({});
  // Commission: inherited from the supplier profile (percent), editable per order, required.
  // If the profile has no rate, the input prefills empty and the operator must fill it in.
  // commissionDirty stops a late-arriving profile fetch from clobbering operator input.
  const [commissionType, setCommissionType] = useState<'percent' | 'fixed'>('percent');
  const [commissionValue, setCommissionValue] = useState('');
  const [commissionDirty, setCommissionDirty] = useState(false);

  const supplierRows = useMemo(
    () => awardedRows.filter(({ quote }) => quote.supplierId === supplier?.value),
    [awardedRows, supplier]
  );

  const resetSelectionFor = (supplierId: number | undefined) => {
    const next: Record<number, { checked: boolean; quantity: string }> = {};
    awardedRows
      .filter(({ quote }) => quote.supplierId === supplierId && !quote.orderId)
      .forEach(({ quote, item }) => {
        next[quote.id] = { checked: true, quantity: String(item.volume ?? '') };
      });
    setSelection(next);
  };

  useEffect(() => {
    if (!isOpen) return;
    const only = supplierOptions.length === 1 ? supplierOptions[0] : null;
    setSupplier(only);
    setBranch(null);
    setSam(null);
    setComment('');
    setSendNotifications(false);
    setCommissionType('percent');
    setCommissionValue('');
    setCommissionDirty(false);
    resetSelectionFor(only?.value);
  }, [isOpen]);

  const { data: branches, isLoading: branchesLoading } = useGetInquiryRestaurantBranchesApi(inquiry.restaurantId, {
    enabled: isOpen && !!inquiry.restaurantId,
  });
  const branchOptions = useMemo<Option[]>(
    () =>
      (branches ?? [])
        .filter((b) => b.isActive !== false)
        .map((b) => ({ value: b.id, label: b.name?.en || b.name?.ar || `Branch #${b.id}` })),
    [branches]
  );
  // The LPO's Deliver-To address comes from the branch — surface it (or its
  // absence) so the operator can fix the branch before sending. Never blocks.
  const selectedBranchAddress = useMemo(
    () => (branch ? (branches ?? []).find((b) => b.id === branch.value)?.address || null : null),
    [branch, branches]
  );

  const { data: samUsers, isLoading: samLoading } = useGetInquirySupplierUsersApi(supplier?.value, {
    enabled: isOpen && !!supplier,
  });
  // Contacts ride on the option so the open menu can render them as a
  // two-line row (see formatSamOption); the label stays the plain name.
  const samOptions = useMemo<SamOption[]>(
    () =>
      (samUsers ?? []).map((u) => ({
        value: u.id,
        label: u.name || u.email || `User #${u.id}`,
        email: u.email,
        phoneNumber: u.phoneNumber,
      })),
    [samUsers]
  );

  const { data: profileCommission } = useGetSupplierProfileCommissionApi(supplier?.value, {
    enabled: isOpen && !!supplier,
  });

  useEffect(() => {
    if (!isOpen || commissionDirty) return;
    setCommissionType('percent');
    setCommissionValue(profileCommission?.commissionRate != null ? String(profileCommission.commissionRate) : '');
  }, [isOpen, profileCommission, commissionDirty]);

  const pickSupplier = (opt: Option | null) => {
    setSupplier(opt);
    setSam(null); // SAM belongs to one supplier
    setCommissionDirty(false); // re-inherit the new supplier's profile rate
    setCommissionValue('');
    setCommissionType('percent');
    resetSelectionFor(opt?.value);
  };

  // Quotes still sendable right now. A background refetch can set orderId on a
  // checked quote while the modal is open (another admin ordered it); the stale
  // selection entry must not count toward canSubmit/total or reach the payload.
  const sendableQuoteIds = useMemo(
    () => new Set(supplierRows.filter(({ quote }) => !quote.orderId).map(({ quote }) => quote.id)),
    [supplierRows]
  );
  const checkedItems = Object.entries(selection).filter(
    ([quoteId, v]) => v.checked && sendableQuoteIds.has(Number(quoteId))
  );
  const hasInvalidQuantity = checkedItems.some(([, v]) => !(Number(v.quantity) > 0));

  const totalPrice = useMemo(() => {
    const priceByQuoteId = new Map(supplierRows.map(({ quote }) => [quote.id, quote.price]));
    return checkedItems.reduce((sum, [quoteId, v]) => {
      const price = priceByQuoteId.get(Number(quoteId)) ?? 0;
      return sum + price * (Number(v.quantity) || 0);
    }, 0);
  }, [checkedItems, supplierRows]);

  const commissionNumber = commissionValue === '' ? null : Number(commissionValue);
  const commissionError = (() => {
    if (commissionNumber == null) return 'Commission is required — set a percent or fixed amount.';
    if (Number.isNaN(commissionNumber)) return 'Enter a valid number';
    if (!/^\d*(\.\d{0,2})?$/.test(commissionValue)) return 'Use at most 2 decimal places';
    if (commissionType === 'percent' && commissionNumber > 100) return 'Percent commission must be between 0 and 100';
    if (commissionType === 'fixed' && commissionNumber <= 0) return 'Fixed commission must be greater than 0';
    return null;
  })();
  // commissionError alone still gates canSubmit/submit — an untouched empty field must block
  // sending. Display is gated separately so the required-but-empty state doesn't flash red
  // before the operator interacts, matching Supplier/Branch/SAM (asterisk-only until touched).
  const showCommissionError = commissionDirty && !!commissionError;

  const currency = supplierRows[0]?.quote.currency || 'AED';
  const commissionPreview = useMemo(() => {
    if (commissionNumber == null || commissionError || totalPrice <= 0) return null;
    if (commissionType === 'percent') {
      return `${commissionNumber}% of ${totalPrice.toFixed(2)} = ${((commissionNumber * totalPrice) / 100).toFixed(
        2
      )} ${currency}`;
    }
    return `${commissionNumber.toFixed(2)} ${currency} = ${((commissionNumber / totalPrice) * 100).toFixed(
      2
    )}% of ${totalPrice.toFixed(2)}`;
  }, [commissionNumber, commissionError, commissionType, totalPrice, currency]);

  const canSubmit =
    !!supplier && !!branch && !!sam && checkedItems.length > 0 && !hasInvalidQuantity && !commissionError;

  const submit = async () => {
    if (!canSubmit || !branch || !sam || commissionNumber == null) return;
    try {
      const result = await sendOrder.mutateAsync({
        id: inquiry.id,
        branchId: branch.value,
        supplierUserId: sam.value,
        items: checkedItems.map(([quoteId, v]) => ({ quoteId: Number(quoteId), quantity: Number(v.quantity) })),
        ...(comment.trim() ? { comment: comment.trim() } : {}),
        commission: { type: commissionType, value: commissionNumber },
        sendNotifications,
      });
      toast({
        title: `Order ${result.code} sent to ${result.supplierName || supplier?.label}`,
        description: `${result.itemsCount} item(s), total ${totalPrice.toFixed(2)}`,
        status: 'success',
      });
      onClose();
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      toast({
        title: 'Failed to send order',
        description: status === 409 ? 'One or more selected quotes were already ordered.' : apiErrorMessage(err),
        status: 'error',
      });
    }
  };

  const selectStyles = {
    menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 2000 }),
  };
  const portalTarget = typeof document !== 'undefined' ? document.body : undefined;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: 'xl' }} scrollBehavior='inside'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Send Order</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl isRequired mb='4'>
            <FormLabel fontSize='sm'>Supplier</FormLabel>
            <ChakraSelect
              options={supplierOptions}
              value={supplier}
              onChange={(opt: any) => pickSupplier(opt || null)}
              isClearable
              placeholder='Supplier with awarded items…'
              menuPortalTarget={portalTarget}
              styles={selectStyles}
            />
          </FormControl>
          <FormControl isRequired mb='4'>
            <FormLabel fontSize='sm'>Delivery Branch</FormLabel>
            <ChakraSelect
              options={branchOptions}
              value={branch}
              isLoading={branchesLoading}
              onChange={(opt: any) => setBranch(opt || null)}
              isClearable
              placeholder='Restaurant branch…'
              menuPortalTarget={portalTarget}
              styles={selectStyles}
            />
            {branch && selectedBranchAddress && (
              <Text fontSize='xs' color='gray.600' mt='1'>
                Delivery address: {selectedBranchAddress}
              </Text>
            )}
            {branch && !selectedBranchAddress && (
              <Text fontSize='xs' color='orange.600' mt='1'>
                This branch has no delivery address — the LPO will be sent without one. Set the address on the branch
                first.
              </Text>
            )}
          </FormControl>
          <FormControl isRequired mb='4'>
            <FormLabel fontSize='sm'>Supplier User (SAM)</FormLabel>
            <ChakraSelect
              options={samOptions}
              value={sam}
              isLoading={samLoading}
              isDisabled={!supplier}
              onChange={(opt: any) => setSam(opt || null)}
              isClearable
              placeholder={supplier ? 'Addressed to…' : 'Select supplier first'}
              formatOptionLabel={formatSamOption}
              filterOption={filterSamOption}
              menuPortalTarget={portalTarget}
              styles={selectStyles}
            />
            {supplier && !samLoading && samOptions.length === 0 && (
              <Text fontSize='xs' color='orange.600' mt='1'>
                This supplier has no active users — the order cannot be addressed.
              </Text>
            )}
          </FormControl>
          {supplier && (
            <FormControl>
              <FormLabel fontSize='sm'>Awarded Items</FormLabel>
              {supplierRows.map(({ quote, item }) => {
                const sel = selection[quote.id];
                const ordered = !!quote.orderId;
                return (
                  <Flex key={quote.id} align='center' gap='3' py='1.5' opacity={ordered ? 0.5 : 1} flexWrap='wrap'>
                    <Checkbox
                      isChecked={!!sel?.checked}
                      isDisabled={ordered}
                      onChange={(e) =>
                        setSelection((prev) => ({
                          ...prev,
                          [quote.id]: { checked: e.target.checked, quantity: prev[quote.id]?.quantity ?? '' },
                        }))
                      }
                    />
                    <Text fontSize='sm' flex='1' noOfLines={1}>
                      {quote.product?.nameEn || `SKU #${quote.productId}`}
                      {/* A 0 is auto-quotation's "not priced" placeholder —
                          saying so beats sending "0 AED" to a supplier. */}
                      <Text as='span' color={isPriced(quote.price) ? 'gray.500' : 'red.500'}>
                        {' '}
                        {isPriced(quote.price) ? `— ${quote.price} ${quote.currency}` : '— no price yet'}
                      </Text>
                    </Text>
                    {ordered ? (
                      <Badge colorScheme='purple' variant='subtle'>
                        Ordered
                      </Badge>
                    ) : (
                      <Input
                        size='sm'
                        width={{ base: '72px', md: '90px' }}
                        value={sel?.quantity ?? ''}
                        isDisabled={!sel?.checked}
                        onChange={(e) =>
                          setSelection((prev) => ({
                            ...prev,
                            [quote.id]: { checked: true, quantity: numOnly(e.target.value) },
                          }))
                        }
                        placeholder='Qty'
                      />
                    )}
                    <Text fontSize='xs' color='gray.400' width='60px' textAlign='end'>
                      {item.masterProduct?.baseUnit || ''}
                    </Text>
                  </Flex>
                );
              })}
              {supplierRows.length === 0 && (
                <Text fontSize='sm' color='gray.500'>
                  No awarded items for this supplier.
                </Text>
              )}
            </FormControl>
          )}
          <FormControl mt='4' isRequired isInvalid={showCommissionError}>
            <FormLabel fontSize='sm'>Commission</FormLabel>
            <Flex gap='2' direction={{ base: 'column', sm: 'row' }}>
              <Box width={{ base: 'full', sm: '150px' }} flexShrink={0}>
                <ChakraSelect
                  options={[
                    { value: 'percent', label: 'Percent (%)' },
                    { value: 'fixed', label: `Fixed (${currency})` },
                  ]}
                  value={{
                    value: commissionType,
                    label: commissionType === 'percent' ? 'Percent (%)' : `Fixed (${currency})`,
                  }}
                  onChange={(opt: any) => {
                    setCommissionType(opt?.value ?? 'percent');
                    setCommissionDirty(true);
                  }}
                  isSearchable={false}
                  menuPortalTarget={portalTarget}
                  styles={selectStyles}
                />
              </Box>
              <Input
                width={{ base: 'full', sm: '140px' }}
                value={commissionValue}
                onChange={(e) => {
                  setCommissionValue(numOnly(e.target.value));
                  setCommissionDirty(true);
                }}
                placeholder={commissionType === 'percent' ? 'e.g. 5' : 'e.g. 150'}
              />
            </Flex>
            {showCommissionError && (
              <Text fontSize='xs' color='red.500' mt='1'>
                {commissionError}
              </Text>
            )}
            {!showCommissionError && commissionPreview && (
              <Text fontSize='xs' color='gray.600' mt='1'>
                {commissionPreview}
              </Text>
            )}
            {!showCommissionError && !commissionPreview && (
              <Text fontSize='xs' color='gray.500' mt='1'>
                Inherited from the supplier profile — editable for this order.
              </Text>
            )}
          </FormControl>
          <FormControl mt='4'>
            <FormLabel fontSize='sm'>Additional Comments</FormLabel>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder='Special requests from either party — appears on the LPO under Additional Comments'
              rows={3}
              maxLength={2000}
            />
          </FormControl>
          <FormControl mt='4'>
            <Checkbox isChecked={sendNotifications} onChange={(e) => setSendNotifications(e.target.checked)}>
              <Text fontSize='sm'>Send order notifications</Text>
            </Checkbox>
            <Text fontSize='xs' color='gray.500' mt='1'>
              When unchecked, no email, WhatsApp, chat or push notifications are sent to the restaurant or supplier.
            </Text>
          </FormControl>
          {checkedItems.length > 0 && (
            <Text fontSize='sm' fontWeight='semibold' mt='3'>
              Total: {totalPrice.toFixed(2)} {currency}
            </Text>
          )}
        </ModalBody>
        <ModalFooter gap='2'>
          <Button variant='outline' onClick={onClose} isDisabled={sendOrder.isLoading}>
            Cancel
          </Button>
          <Button
            colorScheme='red'
            onClick={submit}
            isDisabled={!canSubmit}
            isLoading={sendOrder.isLoading}
            loadingText='Sending…'>
            Send Order
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
